import {BehaviorSubject, Observable} from 'rxjs';
import {map, switchMap, startWith, shareReplay, filter, take} from 'rxjs/operators';
import {languageRecord} from '@px/const/languages';
import {defaultLangByHost, isLocalizedSite} from '@px/helper/localized-site';
import {env} from '@px/helper/env';
import {mc} from '@px/helper/mc';
import {langApi} from '@px/api/lang';
import {userApi} from '@px/api/user';
import {sd} from '@px/da/sd';
import {readLanguage, writeLanguage} from '@px/storage/lang';
import {readLangRecord, writeLangRecord} from '@px/storage/lang-data';
import {authStatus$} from './auth';
import {userInfo$} from './user';
import {CMS_ARTICLE} from '@px/const/cms';

const langManifest = window.langManifest || {};

export const lang$ = new BehaviorSubject<Language>(readLanguage());
export const languageRecord$ = new BehaviorSubject<Record<string, LanguageConfig>>(getValidLangRecord(languageRecord));
export const fixLink$: any | Observable<FixLink> = lang$.pipe(map(convertFixLink), shareReplay(1));
export const fixLinkCMS$: any | Observable<FixLink> = lang$.pipe(map(convertFixLinkCMS), shareReplay(1));
export const LG$ = lang$.pipe(
  switchMap((lang: Language) => loadLangData(lang)),
  startWith({language: lang$.getValue(), langRecord: {}}), //  init LG value
  map(convertLG),
  shareReplay(1)
);

function getValidLangRecord(languageRecord) {
  if (!isLocalizedSite) {
    return languageRecord;
  }
  return {
    [defaultLangByHost]: languageRecord[defaultLangByHost],
    en: languageRecord.en,
  };
}

export function changeLanguage(language: Language) {
  authStatus$
    .pipe(
      filter((auth) => auth === 'Login'),
      take(1)
    )
    .subscribe(() => updateProfile({lang: language}));

  authStatus$
    .pipe(
      filter((auth) => auth !== 'Login'),
      take(1)
    )
    .subscribe(() => lang$.next(language));
}

lang$.subscribe((language) => {
  const langConfig = languageRecord[language];
  document.documentElement.lang = langConfig ? langConfig.html : 'en-US';
  writeLanguage(language);
  sd.setProfile({language});
});

function convertFixLink(language: Language): FixLink {
  return function (link) {
    if (language === 'en') {
      return link;
    }
    if (link === '/') {
      return '/' + language;
    }
    return '/' + language + link;
  };
}

function convertFixLinkCMS(language: Language): FixLink {
  return function (id: string) {
    // 如果是 http 或者 https 开头直接返回
    if (/^https?:\/\//.test(id)) return id;

    // 如果是 url 添加语言信息
    if (isNaN(Number(id))) {
      if (['en', 'vi'].indexOf(language) > -1) {
        return id;
      }
      return '/' + language + id;
    }

    // 如果是文章 id
    const data = CMS_ARTICLE?.[id];
    const link = data?.[language] || data?.['en'];

    // Fat 环境直接返回 URL
    if (!!env.isFat) {
      return link;
    }
    return 'https://phemex.com' + link;
  };
}

function format(template, args) {
  if (!template) {
    return '';
  }

  return args ? Object.keys(args).reduce((p, c) => p.replace(new RegExp(['(\\{', c, '\\})'].join(''), 'g'), args[c]), template) : template;
}

function convertLG({language, langRecord}) {
  if (env.isDev) {
    return function (field: Record<string, any>, params?: Record<string, any>) {
      if (!field) {
        return '';
      }
      const result = field[language] || field['en'];
      return format(result, params);
    };
  }

  return function (fieldHash, params?: Record<string, any>) {
    if (!fieldHash) {
      console.warn('lang is empty');
      return '';
    }

    const result = langRecord[fieldHash];
    return format(result, params);
  };
}

async function loadLangData(language: Language) {
  if (env.isDev) {
    return {language, langRecord: {}};
  }

  const langRecord = readLangRecord(language);
  if (langRecord) {
    return {language, langRecord};
  }

  const {reply} = await langApi.load(`/trade/lang/${language}-${langManifest[language]}.json`);
  if (!reply) {
    return {language, langRecord: {}};
  }

  writeLangRecord(language, reply);
  return {language, langRecord: readLangRecord(language)};
}

async function updateProfile(params) {
  const {data, error} = await userApi.updateProfile(params);
  if (data) {
    userInfo$.next(data);
    const {lang} = params;
    if (lang) {
      lang$.next(lang);
    }
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}
